import { css } from '@emotion/css';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import React, { useEffect, useRef, useState } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import MenuList from '@mui/material/MenuList';
import { COLOR_BORDER_PRIMARY, COLOR_TEXT_PRIMARY } from './theme';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import MenuProvider from '../core-providers/MenuProvider';
import { optionallyRenderIcon } from './renderIcon';
const StyledButton = styled(Button, {
    name: 'MenuButton',
})(() => ({
    color: COLOR_TEXT_PRIMARY,
    fontWeight: 400,
    fontSize: '1.18rem',
    minWidth: '48px',
    padding: '0.25em 0.5em',
    textTransform: 'none',
}));
const altFocusedLabelStyle = css({
    ":first-letter": {
        textDecoration: 'underline',
    },
});
const MenuButton = ({ altFocused, itemId, label, menuItems, open, onButtonClicked, onClickedAway, onHovered, onMenuItemClicked, }) => {
    const buttonRef = useRef(null);
    const buttonId = itemId;
    const menuId = `${itemId}-menu`;
    const closeMenuItemWith = (onClick) => (e) => {
        onClick(e);
        onMenuItemClicked();
    };
    const handleListKeyDown = (event) => {
        if (event.key === 'Tab') {
            event.preventDefault();
            onClickedAway();
        }
        else if (event.key === 'Escape') {
            onClickedAway();
        }
    };
    return (React.createElement("span", null,
        React.createElement(StyledButton, { id: buttonId, ref: buttonRef, "aria-controls": open ? menuId : undefined, "aria-expanded": open, onClick: onButtonClicked, onMouseOver: onHovered, variant: open ? "outlined" : "text", sx: open ? undefined : { border: '1px solid rgba(0,0,0,0)' } },
            React.createElement("span", { className: altFocused ? altFocusedLabelStyle : undefined }, label)),
        React.createElement(Popper, { open: open, anchorEl: buttonRef?.current, role: "button", placement: "bottom-start", disablePortal: true, sx: { zIndex: 100, border: `1px solid ${COLOR_BORDER_PRIMARY}` } },
            React.createElement(Paper, null,
                React.createElement(ClickAwayListener, { onClickAway: onClickedAway },
                    React.createElement(MenuList, { autoFocusItem: open, id: menuId, "aria-labelledby": buttonId, dense: true, onKeyDown: handleListKeyDown, sx: { minWidth: '250px' } }, menuItems.map((menuItem) => {
                        if (menuItem.divider)
                            return React.createElement(Divider, null);
                        const action = menuItem;
                        return (React.createElement(MenuItem, { id: action.id, onClick: closeMenuItemWith(action.run), disabled: action.disabled },
                            Boolean(action.icon) && (optionallyRenderIcon(action.icon, { fontSize: 'small', sx: { color: 'text.secondary', marginRight: '0.5em' } })),
                            React.createElement(ListItemText, { id: `${action.id}-label`, sx: { marginRight: '2em' } }, action.label),
                            Boolean(action.iconSuffix) && (optionallyRenderIcon(action.iconSuffix, { fontSize: 'small', sx: { color: 'text.secondary' } })),
                            Boolean(action.hotkey) && (React.createElement(Typography, { variant: "body2", sx: { color: 'text.secondary' } }, action.hotkey))));
                    })))))));
};
const MenuBar = ({ className, ipc, }) => {
    const [isMenuAltFocused, setIsMenuAltFocused] = useState(false);
    const [isMenuHoverable, setIsMenuHoverable] = useState(false);
    const [openMenuId, setOpenMenuId] = useState(null);
    const toggleAltFocus = setIsMenuAltFocused.bind(null, !isMenuAltFocused);
    const menus = MenuProvider.provide(ipc);
    useEffect(() => {
        const handler = (e) => {
            if (e.key === 'Alt' && !e.ctrlKey && !e.shiftKey) {
                e.stopPropagation();
                e.preventDefault();
                return toggleAltFocus();
            }
            if (isMenuAltFocused) {
                menus.forEach(({ id, label }) => {
                    const altFocusKey = label.toLowerCase().substring(0, 1);
                    if (e.key === altFocusKey && !e.ctrlKey && !e.shiftKey) {
                        toggleAltFocus();
                        setOpenMenuId(id);
                        setIsMenuHoverable(true);
                    }
                });
            }
        };
        window.addEventListener('keyup', handler);
        return () => window.removeEventListener('keyup', handler);
    }, [isMenuAltFocused]);
    const onButtonClicked = (menuId) => {
        if (openMenuId === menuId) {
            setOpenMenuId(null);
            setIsMenuHoverable(false);
        }
        else {
            setOpenMenuId(menuId);
            setIsMenuHoverable(true);
        }
    };
    const onClickAway = (menuId) => {
        if (openMenuId === menuId) {
            setOpenMenuId(null);
            setIsMenuHoverable(false);
        }
    };
    const onHovered = (menuId) => {
        if (isMenuHoverable && openMenuId !== menuId) {
            setOpenMenuId(menuId);
        }
    };
    const onMenuItemClicked = () => {
        setOpenMenuId(null);
        setIsMenuHoverable(false);
    };
    return (React.createElement(Stack, { id: "menu-bar", direction: "row", className: className }, menus.map((menu) => (React.createElement(MenuButton, { key: menu.id, itemId: menu.id, altFocused: isMenuAltFocused, label: menu.label, menuItems: menu.menuItems, open: openMenuId === menu.id, onButtonClicked: onButtonClicked.bind(null, menu.id), onClickedAway: onClickAway.bind(null, menu.id), onHovered: onHovered.bind(null, menu.id), onMenuItemClicked: onMenuItemClicked })))));
};
export default MenuBar;
//# sourceMappingURL=MenuBar.js.map